import { mat4 } from "gl-matrix"
import { Texture, TextureOptions } from "@/core/backends/webgl/texture"
import { clamp } from "@/core/utils/math"
import { getPerspectiveMatrix } from "@/core/perspective"
import { WebGLBackend } from "@/core/backends/webgl-backend"
import { Photo } from "@/core/photo"
import { ProfileManager } from "@/core/profile-manager"
import { DisplayOutputPass } from "@/core/pipeline/passes/display-output-pass"
import { AdjustmentsPass } from "@/core/pipeline/passes/adjustments-pass"
import { TransformPass } from "./passes/transform-pass"
import { PreprocessPass } from "./passes/preprocess-pass"
import { Mask, RenderColorSpace } from "@/core/types"
import { RefineMaskPass } from "@/core/pipeline/passes/refine-mask-pass"
import { AssetResolver, AssetResolverManager } from "../asset-resolver"
import { loadImageBitmap } from "../utils/load-image"
import { BrushMaskPass } from "./passes/brush-mask-pass"
import { WaveletDenoisePass, WaveletTextures } from "./passes/wavelet-denoise-pass"
import { DualBuffers } from "./dual-buffers"
import filmGrainUrl from "@assets/grain/film-grain.png?url"
import { ColorSampler } from "../color-sampler"
import { HealingPass } from "./passes/healing-pass"
import { PerformanceCounters } from "@/utils/measurement/performance-counters"
import { TextureArray } from "../backends/webgl/texture-array"
import { MaskArrayPass, maskSize, minMaskLayers, renderMask } from "./passes/mask-array-pass"
import { RawProfilePass } from "./passes/raw-profile-pass"
import { getDefaultAdjustments } from "../adjustments"
import { Histogram } from "../histogram"
import { HealingSourceDetector } from "../healing-source-detector"
import { RGBPlaneOutput, RGBPlaneOutputPass } from "./passes/rgb-plane-output-pass"
import { YUVOutput, YUVOutputPass } from "./passes/yuv-output-pass"

export type RendererOutput =
	| { format: "rgba"; texture?: Texture }
	| ({ format: "yuv" } & YUVOutput)
	| ({ format: "rgb-planar" } & RGBPlaneOutput)

/**
 * Pipeline stages
 * --------------
 * Pre pass:
 * 	1. Denoise texture
 * 	2. Texture maps (dehaze, clarity, texture)
 *
 * Main pass:
 * 	1. Distortion (lens, perspective) + denoise + crop
 * 	2. Adjustments
 * 		a. White balance
 * 		b. Profile LUTs
 * 		c. Exposure
 * 		d. Tonemap
 * 		c. Adjustments
 *  3. Masks
 * 		a. Inverse tonemap
 * 		b. Adjustments...
 *
 * Final pass:
 *  1. Linear to output color space conversion
 *
 * Post pass (viewport)
 *  1. Render in viewport (final pass texture + apply view matrix)
 *
 */

export class Renderer {
	label: string

	backend: WebGLBackend
	profileManager: ProfileManager
	colorSampler: ColorSampler

	histogram: Histogram
	healingSourceDetector: HealingSourceDetector

	performanceCounters: PerformanceCounters
	assets: AssetResolverManager

	identityMatrix = Object.freeze(mat4.create())

	// Textures
	dualBuffers: DualBuffers
	originalDualBuffers: DualBuffers
	chunkingBuffers: DualBuffers

	blurBuffers: DualBuffers
	maskBuffers: DualBuffers
	maskArray: TextureArray

	sourceTexture: Texture

	scratchTexture: Texture
	scratchBuffers: DualBuffers

	alphaTexture: Texture

	inputTexture: Texture
	originalInputTexture: Texture

	previewTexture: Texture
	originalPreviewTexture: Texture

	denoiseTextures?: {
		denoiseLumTexture: Texture
		denoiseColorTexture: Texture
	}

	chunkDenoiseTextures: {
		denoiseLumTexture: Texture
		denoiseColorTexture: Texture
	}

	denoiseEnabled = false

	waveletTextures?: WaveletTextures

	filteredTextures: {
		meanA: Texture
		meanB: Texture
	}

	// Adjustment curves textures
	// TODO: These can be moved to AdjustmentsPass

	parametricAndContrastCurveTexture: Texture
	adjustmentsCurveTexture: Texture
	adjustmentsHslTexture: Texture

	// For local WB adjustments
	wbGainTexture: Texture

	// Grain
	grainTexture?: Texture

	// Computed state
	photo: Photo
	viewMatrix = this.identityMatrix
	cropMatrix = this.identityMatrix
	perspectiveMatrix = this.identityMatrix

	// Passes - run every time a photo is re-rendered

	preprocessPass: PreprocessPass
	rawProfilePass: RawProfilePass
	transformPass: TransformPass
	adjustmentsPass: AdjustmentsPass
	displayOutputPass: DisplayOutputPass
	rgbPlaneOutputPass: RGBPlaneOutputPass
	yuvOutputPass: YUVOutputPass
	brushMaskPass: BrushMaskPass
	maskArrayPass: MaskArrayPass
	healingPass: HealingPass

	// Utility shader-rendering pass - only runs when needed

	refineMaskPass: RefineMaskPass // runs when detecting masks
	waveletDenoisePass: WaveletDenoisePass // runs when loading raw and jpeg

	constructor(
		backend: WebGLBackend,
		options: {
			label?: string
			resolvers?: AssetResolver[]
			isHistogramEnabled?: boolean | null
		},
	) {
		this.label = options.label ?? "default"
		this.backend = backend

		this.profileManager = new ProfileManager(backend)
		this.colorSampler = new ColorSampler(this)
		this.assets = new AssetResolverManager(this, options.resolvers ?? [])

		this.performanceCounters = new PerformanceCounters(this.backend.gl)
		//this.performanceCounters.isEnabled = true

		this.histogram = new Histogram(this, {
			isEnabled: options.isHistogramEnabled ?? true,
		})

		this.healingSourceDetector = new HealingSourceDetector(this)

		// Setup textures

		const textureOptions: TextureOptions = {
			label: "renderer.untitledTexture",
			format: backend.textureFormat,
		}

		this.dualBuffers = new DualBuffers(backend, "renderer.dualBuffers")
		this.originalDualBuffers = new DualBuffers(backend, "renderer.originalDualBuffers")
		this.blurBuffers = new DualBuffers(backend, "renderer.blurDualBuffers")

		this.chunkingBuffers = new DualBuffers(backend, "renderer.chunkingBuffers")

		this.scratchBuffers = new DualBuffers(backend, "renderer.scratchBuffers", {
			label: "renderer.scratchBuffers",
			format: "r8unorm",
			width: 0,
			height: 0,
		})

		this.chunkDenoiseTextures = {
			denoiseLumTexture: backend.createTexture({
				label: "denoise.lumChunkTexture",
				format: "r16float",
			}),
			denoiseColorTexture: backend.createTexture({
				label: "denoise.colorChunkTexture",
				format: "rg16float",
			}),
		}

		this.sourceTexture = backend.createTexture({
			label: "renderer.sourceTexture",
			min: "nearest",
			mag: "nearest",
		})

		this.maskBuffers = new DualBuffers(backend, "renderer.maskDualBuffers", {
			label: "renderer.maskBuffers",
			format: "r8unorm",
		})

		this.maskArray = backend.createTextureArray({
			label: "renderer.maskTextureArray",
			width: maskSize,
			height: maskSize,
			layers: minMaskLayers,
			format: "r8unorm",
		})

		this.scratchTexture = backend.createTexture({ label: "renderer.scratchTexture" })

		this.previewTexture = backend.createTexture({
			label: "renderer.previewTexture",
			format: "rgba8unorm",
		})

		this.originalPreviewTexture = backend.createTexture({
			label: "renderer.originalPreviewTexture",
			format: backend.textureFormat,
		})

		this.alphaTexture = backend.createTexture({
			label: "renderer.alphaTexture",
			format: "r8unorm",
		})

		this.inputTexture = backend.createTexture({
			...textureOptions,
			label: "renderer.inputTexture",
			mips: true,
			min: "linear",
		})

		this.originalInputTexture = backend.createTexture({
			...textureOptions,
			label: "renderer.originalInputTexture",
		})

		this.filteredTextures = {
			meanA: backend.createTexture({
				...textureOptions,
				label: "renderer.filteredTextures.meanA",
			}),
			meanB: backend.createTexture({
				...textureOptions,
				label: "renderer.filteredTextures.meanB",
			}),
		}

		this.parametricAndContrastCurveTexture = backend.createTexture({
			label: "renderer.parametricAndContrastCurveTexture",
			format: "rg32float",
		})

		this.adjustmentsCurveTexture = backend.createTexture({
			label: "renderer.adjustmentsCurveTexture",
			format: "rgba32float",
		})

		this.adjustmentsHslTexture = backend.createTexture({
			label: "renderer.adjustmentsHslTexture",
			format: "rgb32float",
		})

		this.wbGainTexture = backend.createTexture({
			label: "renderer.adjustmentsHslTexture",
			format: "rgb32float",
		})

		// Grain texture

		loadImageBitmap(filmGrainUrl, undefined, { premultiplyAlpha: "none" }).then((value) => {
			this.grainTexture = this.backend.createTexture({
				label: "renderer.grainTexture",
				format: "rgba8unorm",
				wrapS: "repeat",
				wrapT: "repeat",
				data: value,
				mips: true,
			})
			this.grainTexture.generateMipmap()
		})

		// Default photo

		this.photo = new Photo({
			type: "raw",
			texture: this.inputTexture,
		})

		// Setup render passes

		this.preprocessPass = new PreprocessPass(this, "preprocess")
		this.rawProfilePass = new RawProfilePass(this, "raw-profile")
		this.adjustmentsPass = new AdjustmentsPass(this, "adjustments")
		this.transformPass = new TransformPass(this, "lens-correction")
		this.displayOutputPass = new DisplayOutputPass(this, "output")
		this.rgbPlaneOutputPass = new RGBPlaneOutputPass(this, "rgb-plane")
		this.yuvOutputPass = new YUVOutputPass(this, "yuv")
		this.brushMaskPass = new BrushMaskPass(this, "brush-mask")
		this.maskArrayPass = new MaskArrayPass(this, "mask-array")
		this.healingPass = new HealingPass(this, "healing")

		// Setup utility passes

		this.refineMaskPass = new RefineMaskPass(this, "refine-mask")
		this.waveletDenoisePass = new WaveletDenoisePass(this, "wavelet")
	}

	cleanup() {
		this.originalDualBuffers.reset()
		this.maskBuffers.reset()
		this.scratchBuffers.reset()

		this.inputTexture?.reset()
		this.originalInputTexture?.reset()
		this.sourceTexture?.reset()
		this.scratchTexture?.reset()
		this.originalPreviewTexture?.reset()
		this.previewTexture.reset()
		this.alphaTexture.reset()

		this.filteredTextures.meanA.reset()
		this.filteredTextures.meanB.reset()

		this.parametricAndContrastCurveTexture.reset()
		this.adjustmentsCurveTexture.reset()
		this.adjustmentsHslTexture.reset()
		this.wbGainTexture.reset()
		this.adjustmentsPass.invalidateCache()

		this.chunkingBuffers.reset()
		this.chunkDenoiseTextures.denoiseColorTexture.reset()
		this.chunkDenoiseTextures.denoiseLumTexture.reset()

		this.cleanupDenoiseTextures()

		this.brushMaskPass.dispose()
		this.healingPass.dispose()
		this.maskArrayPass.dispose()
		this.preprocessPass.dispose()
		this.healingSourceDetector.dispose()
		this.histogram.dispose()

		this.assets.cleanup()
		this.profileManager.reset()
	}

	cleanupDenoiseTextures() {
		this.denoiseTextures?.denoiseColorTexture.updateSize(0, 0)
		this.denoiseTextures?.denoiseLumTexture.updateSize(0, 0)

		if (this.waveletTextures) {
			this.waveletTextures.lumLevels.forEach((lumLevel) => {
				lumLevel.delete()
			})

			this.waveletTextures.lumLevels = []

			this.waveletTextures.singleChannelTexture.delete()
			this.waveletTextures.tempW2H2.delete()

			this.waveletTextures = undefined
		}

		this.dualBuffers.reset()
		this.blurBuffers.reset()
	}

	get shaders() {
		return this.backend.shaders
	}

	setRenderTarget(target?: Texture, attachments?: Texture[]) {
		this.backend.setFramebuffer(target ?? null, attachments)
	}

	renderWithoutAdjustments(
		photo: Photo,
		options: {
			viewMatrix?: mat4
			cropMatrix?: mat4
			width: number
			height: number
			output?: RendererOutput
			colorSpace?: RenderColorSpace
			ignoreLensCorrection?: boolean
			renderTransformOnly?: boolean
		},
	) {
		const width = clamp(Math.round(options.width), 1, this.backend.maxRenderSize)
		const height = clamp(Math.round(options.height), 1, this.backend.maxRenderSize)

		this.viewMatrix = options.viewMatrix ?? this.identityMatrix
		this.cropMatrix = options.cropMatrix ?? photo.imageState.cropMatrixInv

		this.perspectiveMatrix = getPerspectiveMatrix(
			getDefaultAdjustments(photo),
			photo.initialOrientation,
			photo.imageState.originalImageSize,
		)

		const rwBuffers = this.originalDualBuffers

		rwBuffers.updateSize(width, height)
		this.transformPass.render(photo.texture, rwBuffers.writeBuffer, {
			ignoreLensCorrection: options.ignoreLensCorrection,
		})
		rwBuffers.swapBuffers()

		if (options.renderTransformOnly) {
			return rwBuffers.readBuffer
		}

		if (this.profileManager.embeddedProfile) {
			this.rawProfilePass.render(rwBuffers.readBuffer, rwBuffers.writeBuffer)
			rwBuffers.swapBuffers()
		}

		this.preprocessPass.render(rwBuffers.readBuffer)
		this.maskArrayPass.render()

		this.adjustmentsPass.render(rwBuffers.readBuffer, {
			outputTexture: rwBuffers.writeBuffer,
			adjustments: getDefaultAdjustments(photo),
			shouldUseDefaultProfileAdjustments: true,
		})

		rwBuffers.swapBuffers()

		return this.renderFinalOutput(rwBuffers.readBuffer, options.output, rwBuffers.writeBuffer, {
			outputColorSpace: options.colorSpace ?? "display-p3",
			showHighlightsClipping: false,
			showShadowsClipping: false,
		})
	}

	render(
		photo: Photo,
		options: {
			colorSpace?: RenderColorSpace
			ignoreCrop?: boolean
			output?: RendererOutput
			maskOverlayIds?: Set<string> | null
			maskOverlayPreviewMasks?: Mask[] | null
			width: number
			height: number
			buffers?: DualBuffers
			showHighlightsClipping?: boolean
			showShadowsClipping?: boolean
			enableDither?: boolean
			disableHealing?: boolean
			disableSuperSampling?: boolean
		},
	) {
		this.performanceCounters.beginLogicalBlock(
			`renderer-pipeline-${options.width}-${options.height}`,
		)

		const width = clamp(Math.round(options.width), 1, this.backend.maxRenderSize)
		const height = clamp(Math.round(options.height), 1, this.backend.maxRenderSize)
		const healingDisabled = !this.healingPass.enabled || (options?.disableHealing ?? false)

		// Compute state

		this.photo = photo
		this.viewMatrix = this.identityMatrix
		this.cropMatrix = options.ignoreCrop ? this.identityMatrix : photo.imageState.cropMatrixInv

		this.perspectiveMatrix = getPerspectiveMatrix(
			photo.adjustments,
			photo.initialOrientation,
			photo.imageState.originalImageSize,
		)

		// Setup buffers
		const rwBuffers = options?.buffers ?? this.dualBuffers

		// ================================
		// === Start Pipeline Rendering ===
		// ================================

		// Transform

		if (!healingDisabled) {
			// transform pass will be run in healingPass
			this.healingPass.render(rwBuffers, [width, height])
		} else {
			rwBuffers.updateSize(width, height)
			this.transformPass.render(photo.texture, rwBuffers.writeBuffer, {
				disableSuperSampling: options.disableSuperSampling ?? false,
			})
			rwBuffers.swapBuffers()
		}

		if (this.profileManager.embeddedProfile) {
			this.rawProfilePass.render(rwBuffers.readBuffer, rwBuffers.writeBuffer)
			rwBuffers.swapBuffers()
		}

		this.preprocessPass.render(rwBuffers.readBuffer)

		// Prepare masks for local adjustments
		this.performanceCounters.begin("prepareMasks")

		this.brushMaskPass.render(photo.texture)
		this.maskArrayPass.render()

		this.performanceCounters.end("prepareMasks")

		// Global + Local adjustments in one pass

		this.adjustmentsPass.render(rwBuffers.readBuffer, {
			adjustments: photo.adjustments,
			outputTexture: rwBuffers.writeBuffer,
		})

		rwBuffers.swapBuffers()

		if (options.maskOverlayIds || options.maskOverlayPreviewMasks) {
			this.renderMaskOverlays(
				photo,
				options.maskOverlayIds ?? new Set(),
				rwBuffers.readBuffer,
				rwBuffers.writeBuffer,
				options.maskOverlayPreviewMasks ?? [],
			)
		}

		// render ongoing healing mask
		if (!healingDisabled) {
			this.healingPass.renderHealingMask(rwBuffers)
		}

		// Render final output

		const outputTexture = this.renderFinalOutput(
			rwBuffers.readBuffer,
			options.output,
			rwBuffers.writeBuffer,
			{
				outputColorSpace: options?.colorSpace ?? "display-p3",
				showHighlightsClipping: options.showHighlightsClipping,
				showShadowsClipping: options.showShadowsClipping,
				enableDither: options.enableDither,
			},
		)

		if (outputTexture) {
			this.colorSampler.setPreviewTexture(outputTexture)
		}

		this.performanceCounters.endLogicalBlock(
			`renderer-pipeline-${options.width}-${options.height}`,
		)

		return outputTexture
	}

	private renderFinalOutput(
		inputTexture: Texture,
		output: RendererOutput | undefined,
		fallbackTexture: Texture,
		options: {
			outputColorSpace: RenderColorSpace
			showHighlightsClipping?: boolean
			showShadowsClipping?: boolean
			enableDither?: boolean
		},
	) {
		switch (output?.format) {
			case "yuv":
				this.yuvOutputPass.render(inputTexture, output, {
					outputColorSpace: options.outputColorSpace,
				})
				return null

			case "rgb-planar":
				this.rgbPlaneOutputPass.render(inputTexture, output, {
					outputColorSpace: options.outputColorSpace,
				})
				return null

			case "rgba":
			default: {
				const outputTexture = output?.texture ?? fallbackTexture
				this.displayOutputPass.render(inputTexture, outputTexture, {
					outputColorSpace: options.outputColorSpace,
					showHighlightsClipping: options.showHighlightsClipping,
					showShadowsClipping: options.showShadowsClipping,
					enableDither: options.enableDither,
				})

				return outputTexture
			}
		}
	}

	private renderMaskOverlays(
		photo: Photo,
		ids: Set<string>,
		input: Texture,
		output: Texture,
		previewMasks: Mask[] = [],
	) {
		for (const mask of previewMasks) {
			renderMask(this, { mask, input, output })
		}

		if (!photo.adjustments.localAdjustments) {
			return
		}

		for (const group of photo.adjustments.localAdjustments) {
			for (const mask of group.masks) {
				if (ids.has(group.id) && group.active) {
					// Render entire group
					renderMask(this, { mask, input, output })
				} else if (ids.has(mask.id)) {
					// If there is only a single mask, ensure that blend mode is set to "add",
					// otherwise if it's set to "subtract" there will be nothing to render!
					const blendModeOverride = ids.size === 1 ? "add" : undefined
					renderMask(this, { mask, input, output, blendModeOverride })
				}
			}
		}
	}
}
